import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import { createBootstrap } from 'bootstrap-vue-next'
import PublicLayout from './layouts/PublicLayout.vue'
import routes from './router/public-routes'
import { useAuthStore } from './stores/auth'
import { i18n } from './i18n'
import { initSentry } from './config/sentry'
import { registerChunkLoadRecovery, registerRouteLoadErrorAlert } from './utils/chunkReload'

// Import Bootstrap CSS, BootstrapVueNext CSS, FontAwesome and custom styles
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import './styles/public.css'

let authInitialized = false

export const createApp = ViteSSG(
  PublicLayout,
  { routes },
  ({ app, router, initialState, isClient, routePath }) => {
    // 從路由中提取語言，支援根路徑
    let locale = 'en'
    if (routePath) {
      const pathParts = routePath.split('/')
      // 如果是根路徑或根路徑的子頁面，使用英文
      if (pathParts.length <= 2 && !['en', 'zh-tw', 'zh-cn', 'ja'].includes(pathParts[1])) {
        locale = 'en'
      } else {
        locale = pathParts[1] || 'en'
      }
    }
    i18n.global.locale.value = locale

    // SSG build 在 Node 環境沒有 window，只能在瀏覽器端註冊
    if (isClient) {
      initSentry(app)
      registerChunkLoadRecovery()
      registerRouteLoadErrorAlert(router)
    }

    // 設定 Pinia
    const pinia = createPinia()

    app.use(i18n)
    app.use(pinia)
    app.use(createBootstrap())
    
    // 客戶端專用設定 - BootstrapVueNext 會處理 Bootstrap JS，不需要手動導入
    
    // 同步狀態 (SSR/SSG)
    if (import.meta.env.SSR)
      initialState.pinia = pinia.state.value
    else
      pinia.state.value = initialState.pinia || {}
    
    // 設定全域路由守衛
    router.beforeEach(async (to, from, next) => {
      const authStore = useAuthStore()

      // 1. Initialize Auth state on client-side if not already done
      if (isClient && !authInitialized) {
        console.log('🔄 [Public] Initializing Firebase Auth...')
        await authStore.initializeAuth()
        authInitialized = true
        console.log('✅ [Public] Firebase Auth initialized.')
      }

      // 2. Handle redirection for authenticated users
      if (isClient && authStore.isLoggedIn) {
        const guestOnlyRoutes = ['SignIn', 'SignUp', 'SignInRoot', 'SignUpRoot']
        if (guestOnlyRoutes.includes(to.name as string)) {
          console.log('👤 User is logged in, redirecting to /app')
          // Using window.location for a hard redirect to the other Vue app instance
          window.location.pathname = '/app'
          return // Stop navigation
        }
      }

      // 3. Update language settings
      let newLocale = 'en'
      
      if (to.params.lang) {
        // 多語言路徑 (/:lang/...)
        newLocale = to.params.lang as string
      } else if (to.meta?.locale) {
        // 根路徑路由 (/, /about, ...)
        newLocale = to.meta.locale as string
      }
      
      if (i18n.global.locale.value !== newLocale) {
        i18n.global.locale.value = newLocale
      }
      
      next()
    })
  }
)
